import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CalendarTodayOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DatePickerModal from './DatePickerModal';
import { TIME_FORMATS } from './appConstants';
import './CustomDateRangePicker.scss';

const CustomDateRangePicker = ({ 
  startDate, 
  endDate, 
  startTime,
  dueTime,
  onChange, 
  placeholder,
  label,
  fullWidth = true,
  showTime = false,
  allowSingleSelection = false,
  hideStartDate = false,
  hideDueDate = false,
  onOpen
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (onOpen) onOpen();
  };
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const formattedRange = useMemo(() => {
    if (!startDate && !endDate) return '';
    
    const formatDateTime = (dateStr, timeStr) => {
      let str = '';
      if (dateStr) {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          const m = d.toLocaleString('en-US', { month: 'short' });
          str += `${d.getDate()}/${m}/${d.getFullYear().toString().slice(-2)}`;
        }
        if (timeStr && typeof timeStr === 'string' && timeStr.includes(':')) {
          const [h, m] = timeStr.split(':');
          let hour = parseInt(h, 10);
          const ampm = hour >= 12 ? TIME_FORMATS.PM : TIME_FORMATS.AM;
          hour = hour % 12 || 12;
          str += ` ${hour}:${m} ${ampm}`;
        }
      }
      return str;
    };

    const start = formatDateTime(startDate, startTime);
    const end = formatDateTime(endDate, dueTime);

    if (startDate && endDate) return `${start} - ${end}`;
    if (startDate) return `${start} - ...`;
    if (endDate) return hideStartDate ? end : `... - ${end}`;
    return '';
  }, [startDate, endDate, startTime, dueTime, hideStartDate]);

  return (
    <Box className="custom-date-range-picker">
      {label && <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>{label}</Typography>}
      <Box 
        className="range-input-container" 
        onClick={handleOpen}
        sx={{ width: fullWidth ? '100%' : 'auto' }}
      >
        <CalendarTodayOutlined sx={{ fontSize: 14 }} />
        <Typography className={`range-text ${!formattedRange ? 'placeholder' : ''}`}>
          {formattedRange || placeholder || t('common.selectDateRange')}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: 14 }} />
      </Box>

      <DatePickerModal
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        mode="dateRange"
        allowSingleSelection={allowSingleSelection}
        title={t('calendar.selectDates')}
        selectedStartDate={startDate}
        selectedDueDate={endDate}
        selectedDates={startDate && endDate ? [startDate, endDate] : (startDate ? [startDate] : (endDate ? [endDate] : []))}
        minDays={1}
        maxDays={3650}
        showTime={showTime}
        selectedStartTime={startTime}
        selectedDueTime={dueTime}
        hideStartDate={hideStartDate}
        hideDueDate={hideDueDate}
        onApply={(result) => {
          onChange({
            startDate: result.startDate || result.fromDate,
            endDate: result.dueDate || result.toDate,
            startTime: result.startTime,
            dueTime: result.dueTime
          });
        }}
        onClearStartDate={() => {
          onChange({ startDate: null, endDate, startTime: null, dueTime });
        }}
        onClearDueDate={() => {
          onChange({ startDate, endDate: null, startTime, dueTime: null });
        }}
        onDateChange={(dates) => {
          const selection = Array.isArray(dates) ? dates : [];
          let newStart = selection[0] || null;
          let newEnd = selection.length > 1 ? selection[selection.length - 1] : null;
          
          if (selection.length === 1) {
            if (startDate && !endDate) {
              newEnd = null;
            } else if (!startDate && endDate) {
              newEnd = newStart;
              newStart = null;
            }
          }
          if (newStart && newEnd && new Date(newStart) > new Date(newEnd)) {
            [newStart, newEnd] = [newEnd, newStart];
          }
          onChange({ startDate: newStart, endDate: newEnd, startTime, dueTime });
        }}
      />
    </Box>
  );
};

export default CustomDateRangePicker;
